import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { HeartPulse, ScanFace, Brain, Activity } from "lucide-react";

const ICONS = { cardiac: HeartPulse, oncology: ScanFace, mental_health: Brain, metabolic: Activity };
const BAND_TONE = {
  none: "bg-[#EFEBE0] text-[#7B6157] border-[#EFEBE0]",
  low: "bg-[#E8F5E9] text-[#2E7D32] border-[#C8E6C9]",
  moderate: "bg-[#FFF3E0] text-[#8D5A00] border-[#FFECB3]",
  high: "bg-[#FFEBEE] text-[#C62828] border-[#FFCDD2]",
};

export default function FamilyRiskCard({ clientId, onOpenMedical }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!clientId) return;
    api.get(`/clients/${clientId}/family-risk`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [clientId]);
  if (!data) return null;
  const anyHits = Object.values(data.scores).some((s) => s.score > 0);
  return (
    <div className="rounded-2xl border border-[#EFEBE0] bg-white p-5" data-testid="family-risk-card">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div>
          <div className="text-[10px] font-bold uppercase text-[#7B6157]">Family Risk Score</div>
          <h3 className="font-heading text-base font-bold text-[#3E2723]">Genetic risk pattern</h3>
        </div>
        <span className="text-xs text-[#7B6157]">{data.family_member_count} family member{data.family_member_count === 1 ? "" : "s"} on file</span>
      </div>
      {data.family_member_count === 0 ? (
        <p className="text-sm text-[#84675B]">Add family health history to compute risk.</p>
      ) : !anyHits ? (
        <p className="text-sm text-[#84675B]">No known conditions across recorded family members.</p>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(data.scores).map(([key, s]) => {
            const Icon = ICONS[key] || Activity;
            return (
              <div key={key} data-testid={`family-risk-${key}`}
                className={`rounded-xl border p-3 ${BAND_TONE[s.band] || BAND_TONE.none}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon size={13} />
                  <div className="text-[10px] font-bold uppercase tracking-wide">{s.label}</div>
                </div>
                <div className="font-heading text-lg font-bold capitalize">{s.band === "none" ? "None" : s.band}</div>
                <div className="text-[10px] font-semibold">
                  {s.hits.length === 0 ? "No hits" :
                    `${s.hits.length} hit${s.hits.length === 1 ? "" : "s"}${s.hits[0] ? ` (${s.hits[0].relationship})` : ""}`}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {onOpenMedical && data.family_member_count > 0 && (
        <button className="mt-3 text-xs font-semibold text-[#7B6157] hover:underline" onClick={onOpenMedical} data-testid="family-risk-open">
          Open family health history →
        </button>
      )}
    </div>
  );
}
